import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, User, GitBranch, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { STATUS_STYLE, STATUS_LABEL, inr } from "@/lib/branch";
import { MemberList } from "@/components/NetworkTree";
import { DownlineList } from "@/components/DownlineList";

export default function AdminNetworkExplorer() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openBranch, setOpenBranch] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.get("/admin/network/search", { params: { q } }); setRes(data); }
    catch { toast.error("Search failed"); }
    setBusy(false);
  };
  const open = async (id) => {
    setSel(null); setOpenBranch(null);
    try { const { data } = await api.get(`/admin/network/user/${id}`); setSel(data); }
    catch { toast.error("Could not load user"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-network-explorer">
      <div><h1 className="font-display text-3xl font-bold">Global Network Explorer</h1>
        <p className="text-sm text-[#B91C1C]">Search by user ID, mobile, branch ID, referral code or sponsor ID. Nodes load on demand.</p></div>
      <form onSubmit={search} className="flex gap-2 max-w-xl">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-3 text-[#94A3B8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="EG000004, 98765…, BR000001, ADMIN001" className="input-soft pl-9" data-testid="network-explorer-search-input" /></div>
        <button className="btn-primary" disabled={busy} data-testid="network-explorer-search-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}</button>
      </form>

      {res && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="network-explorer-results">
          <div className="bg-white rounded-2xl border border-[#FECACA] p-4"><h3 className="font-semibold mb-2">Users</h3>
            {res.users.length === 0 && <p className="text-xs text-[#94A3B8]">No users.</p>}
            {res.users.map((u) => <button key={u.id} onClick={() => open(u.id)} className="w-full flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-[#FEF2F2] text-left text-sm" data-testid={`explorer-user-${u.user_code}`}>
              <User className="w-4 h-4" /> {u.name} <span className="text-xs text-[#94A3B8]">{u.user_code} · {u.referral_code} · {u.phone}</span><ChevronRight className="w-4 h-4 ml-auto" /></button>)}
          </div>
          <div className="bg-white rounded-2xl border border-[#FECACA] p-4"><h3 className="font-semibold mb-2">Branches</h3>
            {res.branches.length === 0 && <p className="text-xs text-[#94A3B8]">No additional branches.</p>}
            {res.branches.map((b) => <button key={b.id} onClick={() => open(b.owner_user_id)} className="w-full flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-[#FEF2F2] text-left text-sm" data-testid={`explorer-branch-${b.referral_code}`}>
              <GitBranch className="w-4 h-4" /> {b.branch_code} <span className="text-xs text-[#94A3B8]">{b.referral_code} · {STATUS_LABEL[b.status]}</span><ChevronRight className="w-4 h-4 ml-auto" /></button>)}
          </div>
        </div>
      )}

      {sel && (
        <div className="space-y-4" data-testid="network-explorer-user-panel">
          <div className="bg-white rounded-2xl border border-[#FECACA] p-5 flex flex-wrap gap-6 text-sm">
            <div><div className="text-xs text-[#B91C1C]">Member</div><Link to={`/admin/users/${sel.user.id}`} className="font-bold underline">{sel.user.name} ({sel.user.user_code})</Link></div>
            <div><div className="text-xs text-[#B91C1C]">Mobile</div>{sel.user.phone}</div>
            <div><div className="text-xs text-[#B91C1C]">Own verified sales</div>{inr(sel.own_sales.eligible_sales)}</div>
            <div><div className="text-xs text-[#B91C1C]">Net commission</div>{inr(sel.commission.approved)}</div>
            <div><div className="text-xs text-[#B91C1C]">Team size</div>{sel.user.team_size}</div>
          </div>
          <div className="bg-white rounded-2xl border border-[#FECACA] p-5">
            <h3 className="font-semibold mb-2">Original sponsor chain</h3>
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="explorer-upline-chain">
              {sel.upline_chain.length === 0 && <span className="text-[#94A3B8]">Top of the tree.</span>}
              {sel.upline_chain.map((u, i) => <span key={u.id} className="inline-flex items-center gap-1"><button onClick={() => open(u.id)} className="px-2 py-1 rounded-lg bg-[#FEF2F2] hover:bg-[#FEE2E2]">L{i + 1}: {u.name} ({u.user_code}) via {u.sponsor_branch_code || "—"}</button></span>)}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#FECACA] p-5 space-y-2">
            <h3 className="font-semibold">Branches</h3>
            {sel.branches.map((b) => (
              <div key={b.id}>
                <button onClick={() => setOpenBranch(openBranch === b.id ? null : b.id)} className="w-full flex flex-wrap items-center gap-3 py-2 px-3 rounded-xl border border-[#FEE2E2] hover:bg-[#FEF2F2] text-left text-sm" data-testid={`explorer-user-branch-${b.referral_code}`}>
                  <GitBranch className="w-4 h-4 text-[#DC2626]" /><b>{b.branch_code}</b> {b.name} <span className="font-mono text-xs">{b.referral_code}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span>
                  <span className="text-xs text-[#94A3B8]">{b.direct_referrals} direct · {b.downline_members} downline · {inr(b.eligible_sales)} sales · {inr(b.earned_commission)} commission</span>
                </button>
                {openBranch === b.id && <div className="pl-4 py-2"><MemberList code={b.referral_code} /></div>}
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-[#FECACA] p-5"><h3 className="font-semibold mb-3">All descendants (filter by depth / status)</h3><DownlineList root={sel.user.id} /></div>
        </div>
      )}
    </div>
  );
}
